import { Navigate, Route, Routes, useParams } from 'react-router';
import { AppLayout } from '@/components/layout/app-layout';
import { Spinner } from '@/components/ui/layout';
import { useAuth } from '@/lib/auth';
import { t } from '@/lib/i18n';
import { AccountPage } from '@/pages/account';
import { AdvertisementEditPage, AdvertisementsPage } from '@/pages/advertisements';
import { AlertsPage } from '@/pages/alerts';
import { ArrangementPage } from '@/pages/arrangement';
import { AuditLogPage } from '@/pages/audit-log';
import { BusEditPage, BusesPage } from '@/pages/buses';
import { CampaignEditPage, CampaignsPage } from '@/pages/campaigns';
import { DashboardPage } from '@/pages/dashboard';
import { DataHealthPage } from '@/pages/data-health';
import { DemandPage } from '@/pages/demand';
import { InterestPointEditPage, InterestPointsPage } from '@/pages/interest-points';
import { LineTimetablePage } from '@/pages/line-timetable';
import { LineEditPage, LinesPage } from '@/pages/lines';
import { LoginPage } from '@/pages/login';
import { MobileAppPage } from '@/pages/mobile-app';
import { NewsEditPage, NewsPage } from '@/pages/news';
import { NotificationsPage } from '@/pages/notifications';
import { OutOfServiceAreaEditPage, OutOfServiceAreasPage } from '@/pages/out-of-service';
import { PerformancePage } from '@/pages/performance';
import { PinAdvertisementEditPage, PinAdvertisementsPage } from '@/pages/pin-advertisements';
import {
  PlannerLayout,
  PlannerOverview,
  PlannerSettingsPage,
  PlannerTrips,
  PlannerUsage,
} from '@/pages/planner';
import { ReportDetailPage, ReportsPage } from '@/pages/reports';
import { ScheduleEditPage, SchedulesPage } from '@/pages/schedules';
import { StationEditPage, StationsPage } from '@/pages/stations';
import { UsersPage } from '@/pages/users';
import { ZoneEditPage, ZonesPage } from '@/pages/zones';

export function App() {
  const { user, ready, can } = useAuth();
  if (!ready) return <Spinner label={t('Starting…')} />;
  if (!user) return <LoginPage />;
  const admin = can('admin');
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="arrangement" element={<ArrangementPage />} />
        <Route path="buses" element={<BusesPage />} />
        <Route path="buses/:id" element={<BusEditPage />} />
        {/* A line (zone in the API) has two directions (lines in the API). */}
        <Route path="lines" element={<ZonesPage />} />
        <Route path="lines/:id" element={<ZoneEditPage />} />
        <Route path="directions" element={<LinesPage />} />
        <Route path="directions/:id" element={<LineEditPage />} />
        <Route path="zones" element={<Navigate to="/lines" replace />} />
        <Route path="zones/:id" element={<Redirect to="/lines/:id" />} />
        <Route path="stations" element={<StationsPage />} />
        <Route path="stations/:id" element={<StationEditPage />} />
        <Route path="schedules" element={<SchedulesPage />} />
        <Route path="schedules/directions/:lineId" element={<LineTimetablePage />} />
        <Route path="schedules/lines/:id" element={<Redirect to="/schedules/directions/:id" />} />
        <Route path="schedules/:id" element={<ScheduleEditPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="mobile-app" element={<MobileAppPage />} />
        <Route path="performance" element={<PerformancePage />} />
        <Route path="planner" element={<PlannerLayout />}>
          <Route index element={<PlannerOverview />} />
          <Route path="usage" element={<PlannerUsage />} />
          <Route path="trips" element={<PlannerTrips />} />
          <Route path="settings" element={<PlannerSettingsPage />} />
        </Route>
        <Route path="data-health" element={<DataHealthPage />} />
        <Route path="performance/:id" element={<PerformancePage />} />
        <Route path="out-of-service-areas" element={<OutOfServiceAreasPage />} />
        <Route path="out-of-service-areas/:id" element={<OutOfServiceAreaEditPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="news/:id" element={<NewsEditPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/:id" element={<ReportDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        {admin && (
          <>
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="campaigns/:id" element={<CampaignEditPage />} />
            <Route path="advertisements" element={<AdvertisementsPage />} />
            <Route path="advertisements/:id" element={<AdvertisementEditPage />} />
            <Route path="pin-advertisements" element={<PinAdvertisementsPage />} />
            <Route path="pin-advertisements/:id" element={<PinAdvertisementEditPage />} />
            <Route path="demand" element={<DemandPage />} />
            <Route path="interest-points" element={<InterestPointsPage />} />
            <Route path="interest-points/:id" element={<InterestPointEditPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
          </>
        )}
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Old addresses (before zones were called lines and lines directions). */
function Redirect({ to }: { to: string }) {
  const { id } = useParams();
  return <Navigate to={to.replace(':id', id ?? '')} replace />;
}
